import { FieldValues } from 'react-hook-form'
import {
  DiscardFaxPayload,
  AttachFaxPayload,
  fetchLayoutIdPayload,
  splitFaxPayload,
  bulkAssignPayload,
} from '../types'
import axiosInstance from '../utils/axiosInstance'
import { API_END_POINT, ENTITY_MANAGER_END_POINTS } from '../utils/environment'
import { validateApiParams } from '../utils/functions'

export const discardFaxById = async (payload: DiscardFaxPayload) => {
  return await axiosInstance.patch(
    `${API_END_POINT.DELIVERY}/v1/communications/discard/${payload?.id}?reason=${payload?.reason}&reviewer=${payload?.reviewer}`
  )
}

export const attachCaseById = async (payload: AttachFaxPayload) => {
  return await axiosInstance.patch(
    `${API_END_POINT.DELIVERY}/v1/communications/attach/${payload?.faxId}/case/${payload?.caseId}?reason=${payload?.reason}&reviewer=${payload?.reviewer}`
  )
}

export const fetchEntityIdByNameCorrespondingToSelectedClient = async (
  entityName: string
) => {
  return await axiosInstance.get(
    `${API_END_POINT.ENTITY_MANAGER}entities/name/${entityName}`
  )
}

export const fetchFaxIdCorrespondingData = async (id: number) => {
  return await axiosInstance.get(
    `${API_END_POINT.DELIVERY}/v1/communications/${id}`
  )
}

interface ObjectType {
  [name: string]: string | string[]
}

export interface IntakeTypes {
  entity_name: string
  select: string[]
  related_entities?: {
    [entityName: string]: {
      select: string[]
    }
  }
}

export const fetchWebIdCorrespondingData = async (
  id: number,
  payload: ObjectType
) => {
  validateApiParams(
    { id },
    {
      endpoint: `${API_END_POINT.ENTITY_MANAGER_V3}entity-data/get/hcp_portal_entity/${id}`,
      method: 'POST',
    }
  )
  return await axiosInstance.post(
    `${API_END_POINT.ENTITY_MANAGER_V3}entity-data/get/hcp_portal_entity/${id}`,
    payload
  )
}

export const fetchCommunicationIdCorrespondingData = async (
  id: number,
  payload: IntakeTypes
) => {
  validateApiParams(
    {
      id: id,
    },
    {
      endpoint: `${API_END_POINT.ENTITY_MANAGER_V3}entity-data/get/${payload['entity_name']}/${id}`,
      method: 'POST',
    }
  )
  return await axiosInstance.post(
    `${API_END_POINT.ENTITY_MANAGER_V3}entity-data/get/${payload['entity_name']}/${id}`,
    payload
  )
}

export const fetchFaxUrl = async (id: number) => {
  validateApiParams(
    { id },
    {
      endpoint: `${API_END_POINT.DELIVERY}/v1/communications/get-presigned-url/${id}`,
      method: 'GET',
    }
  )
  return await axiosInstance.get(
    `${API_END_POINT.DELIVERY}/v1/communications/get-presigned-url/${id}`
  )
}

export const fetchLayoutIdApi = async (payload: fetchLayoutIdPayload) => {
  const endpoint = `${API_END_POINT.LAYOUT_MANAGER}layout?type=${payload?.mode}&entityId=${payload?.entityId}&defaultValue=true`
  validateApiParams(
    { mode: payload?.mode, entityId: payload?.entityId },
    {
      endpoint,
      method: 'GET',
    }
  )
  return await axiosInstance.get(endpoint)
}

export const changeFaxStatus = async (
  entityName: string,
  communicationId: number,
  entityId: number,
  layoutId: number,
  data: FieldValues
) => {
  const endpoint = `${API_END_POINT.ENTITY_MANAGER_V3}${ENTITY_MANAGER_END_POINTS.ENTITY_DATA}/${entityName}/${communicationId}`
  validateApiParams(
    { entityName, communicationId, entityId, layoutId },
    {
      endpoint,
      method: 'PUT',
    }
  )
  return await axiosInstance.put(
    endpoint,
    {
      meta_data: {
        entity_id: entityId,
        layout_id: layoutId,
      },
      data: data,
    },
    { params: { entityId, layoutId } }
  )
}

export const splitFaxApi = async (payload: splitFaxPayload) => {
  return await axiosInstance.post(
    `${API_END_POINT.DELIVERY}/v1/communications/split/${payload?.communicationId}/${payload?.userId}`,
    payload?.splitArray
  )
}

export const splitCaseDocumentApi = async (
  caseId: number,
  path: string,
  payload: { pageRanges: number[]; documentName: string }[]
) => {
  return await axiosInstance.post(
    `${API_END_POINT.CORE}v1/split/case/documents/${caseId}?relativePath=${path}`,
    payload
  )
}

export const bulkAssignToAgent = async (payload: bulkAssignPayload) => {
  return await axiosInstance.patch(
    `${API_END_POINT.CORE}queueAgent/assign?queueId=${payload?.queueId}&userId=${payload?.userId}&recordIds=${payload?.recordsIds}`
  )
}

export const fetchHcpFormPdfUrl = async (enrollment_id: string) => {
  const endpoint = `${API_END_POINT.CORE}hcp/microsite/econfig/enrollment/${enrollment_id}/pdf?flag=signed`
  validateApiParams(
    { enrollment_id },
    {
      endpoint,
      method: 'GET',
    }
  )
  return await axiosInstance.get(endpoint)
}

export const discardWebIntakeApi = async (
  communicationId: number,
  entityId: number,
  layoutId: number,
  data: FieldValues
) => {
  const endpoint = `${API_END_POINT.ENTITY_MANAGER_V3}${ENTITY_MANAGER_END_POINTS.ENTITY_DATA}/c_cases/${communicationId}?entityId=${entityId}&layoutId=${layoutId}`
  validateApiParams(
    { communicationId, entityId, layoutId },
    {
      endpoint,
      method: 'PUT',
    }
  )
  return await axiosInstance.put(endpoint, {
    meta_data: {
      entity_id: entityId,
      layout_id: layoutId,
    },
    data: data,
  })
}

export const patchAttachForHcpEntity = async (
  ids: string[],
  entityId: string,
  layoutId: string,
  caseId: string | number,
  enrollmentStatusId: string
) => {
  const endpoint = `${API_END_POINT.ENTITY_MANAGER_V3}entity-data/entities?ids=${ids}`
  validateApiParams(
    { entityId, layoutId, caseId, enrollmentStatusId },
    {
      endpoint,
      method: 'PATCH',
    }
  )
  return await axiosInstance.patch(endpoint, {
    meta_data: {
      entity_id: entityId,
      layout_id: layoutId,
    },
    data: {
      case_id: caseId,
      enrollment_status_id: enrollmentStatusId,
    },
  })
}

export const fetchDocumentIdForHcpEntity = async (
  entityId: string,
  enrollmentId: string
) => {
  const endpoint = `${API_END_POINT.ENTITY_MANAGER_V3}entity-data/get/web_enrollment_document`
  validateApiParams(
    { entityId, enrollmentId },
    {
      endpoint,
      method: 'POST',
    }
  )
  return await axiosInstance.post(endpoint, {
    entity_id: entityId,
    select: ['id', 'document', 'enrollment_id'],
    page: 0,
    size: 10,
    filters: [
      {
        criteria: [
          {
            field_name: 'enrollment_id',
            values: [enrollmentId],
            operation: 'EQUALS',
          },
        ],
      },
    ],
  })
}

export const fetchCaseDocumentIdForHcpEntity = async (
  entityId: string,
  layoutId: string,
  caseId: string | number | undefined,
  documentId: string | undefined,
  enrollmentId: string | undefined,
  caseDocmentTypeId?: number
) => {
  const endpoint = `${API_END_POINT.ENTITY_MANAGER_V3}entity-data/entities`
  validateApiParams(
    { entityId, layoutId },
    {
      endpoint,
      method: 'POST',
    }
  )
  return await axiosInstance.post(endpoint, {
    meta_data: {
      entity_id: entityId,
      layout_id: layoutId,
    },
    data: {
      case_id: caseId,
      document_id: Number(documentId),
      name: enrollmentId,
      case_document_type: { id: caseDocmentTypeId, _state: 'ADDED' },
    },
  })
}

export const fetchPrintDocument = async (ids: string) => {
  return await axiosInstance.get(
    `${API_END_POINT.DELIVERY}/v1/communications/get-presigned-url/bulk-print?recordIds=${ids}`
  )
}

export const markCompleteStatusPrintDoc = async (ids: string) => {
  return await axiosInstance.patch(
    `${API_END_POINT.DELIVERY}/v1/communications/completeStatus?recordIds=${ids}`
  )
}

export const completedFaxIntakeApi = async (
  communicationId: number,
  entityId: number,
  layoutId: number,
  data: FieldValues
) => {
  const endpoint = `${API_END_POINT.ENTITY_MANAGER_V3}entity-data/communications/${communicationId}`
  validateApiParams(
    { communicationId, entityId, layoutId },
    {
      endpoint,
      method: 'PUT',
    }
  )
  return await axiosInstance.put(endpoint, {
    meta_data: {
      entity_id: entityId,
      layout_id: layoutId,
    },
    data: data,
  })
}

export const updateEntityInstanceApi = async (
  entityName: string,
  recordId: number,
  entityId: number,
  layoutId: number,
  data: FieldValues
) => {
  const endpoint = `${API_END_POINT.ENTITY_MANAGER_V3}${ENTITY_MANAGER_END_POINTS.ENTITY_DATA}/${entityName}/${recordId}`
  validateApiParams(
    { entityName, recordId, entityId, layoutId },
    {
      endpoint,
      method: 'PUT',
    }
  )
  return await axiosInstance.put(
    endpoint,
    {
      meta_data: {
        entity_id: entityId,
        layout_id: layoutId,
      },
      data: data,
    },
    { params: { entityId, layoutId } }
  )
}

export const updateEntityInstanceCorrespondingToCaseId = async (
  tableName: string,
  recordId: number,
  entityId: number,
  layoutId: number,
  data: FieldValues,
  relationIdentifier?: string
) => {
  const endpoint = `${API_END_POINT.ENTITY_MANAGER_V3}${ENTITY_MANAGER_END_POINTS.ENTITY_DATA}/entities/${recordId}/${tableName}`
  validateApiParams(
    { tableName, recordId, entityId, layoutId },
    {
      endpoint,
      method: 'PUT',
    }
  )
  return await axiosInstance.put(endpoint, {
    meta_data: {
      entity_id: entityId,
      layout_id: layoutId,
      relation_identifier: relationIdentifier,
    },
    data: data,
  })
}
